import { DesignMaterialPath } from "common/materials/library/designMaterialPath";
import { MaterialTemplateIdToJson } from "common/materials/library/materialTemplateIdToJson";
import type { MaterialSrc } from "common/materials/materialSrc";
import { materialCacheKey } from "common/materials/materialSrc";
import type {
  WebGLRenderer,
  MeshPhysicalMaterial,
  MeshPhysicalMaterialParameters,
  Material,
  Texture as TextureType,
  PixelFormat,
} from "three";
import { BackSide, DoubleSide, FrontSide, RepeatWrapping, Texture } from "three";
import { HTTPMaterialParameterLoader } from "./HTTPMaterialParameterLoader";
import { DebugMaterialColors, MeshHardcodedMaterialType } from "./MaterialFactory";
import {
  includeAdvancedPCFShadows,
  includeCommonMaterialUniforms,
  includeLightProbes,
  includeScreenspaceFX,
  includeSsfxRendererOutput,
} from "./materials/customizeShader";
import { createMaterialForParameters, designMaterialNew, setDesignMaterialOptions } from "./materials/designMaterial";
import { DEFAULT_TEXTURE_ANISOTROPY } from "./materials/designMaterialDefaults";
import type { DesignMaterialLoadOptions } from "./materials/designMaterialRuntimeConfig";
import {
  BakeColorTextureMode,
  DetailRenderMode,
  defaultDesignMaterialLoadOptions,
} from "./materials/designMaterialRuntimeConfig";
import { logMaterialSrc } from "./materials/logMaterialSrc";
import THREEAssetLoader from "./THREEAssetLoader";
import { checkWebGLContextLost } from "./threejs/higharc/checkWebGLContextLost";
import { assignSharedDefines, MaterialSharedParameterGroup } from "./threejs/higharc/MaterialSharedParameterGroup";
import type { ExtendedMaterial } from "./threejs/three-extensions";

const failureMaterialType = MeshHardcodedMaterialType.DEBUG_WHITE_MESH;
type OnBeforeCompileShader = Parameters<Material["onBeforeCompile"]>[0];

function loadedMaterialCacheKey(src: MaterialSrc, hasSSFXRenderer: boolean) {
  return materialCacheKey(src) + ";" + (hasSSFXRenderer ? 1 : 0);
}

interface MaterialCache {
  material: Promise<Material>;
  materialSrc: MaterialSrc;
  hasSSFXRenderer: boolean;
  state: "pending" | "rejected" | "fulfilled";
}

export class HTTPMaterialFactory {
  private readonly designMaterialCache: { [key: string]: MaterialCache } = {};
  private readonly hardcodedMaterials: { [key: string]: Material } = {};
  private readonly textureCache: { [key: string]: [TextureType, Promise<void>] } = {};

  readonly defaultMaterialParams = Object.freeze({
    opacity: 1,
    transparent: false,
    color: 0xeeeeee,
    roughness: 1.0,
    metalness: 0.0,
    reflectivity: 0.0,
    side: FrontSide,
    clipShadows: false,
    shadowSide: DoubleSide,
  });

  private readonly assetLoader: THREEAssetLoader;
  private readonly materialLoader: HTTPMaterialParameterLoader;

  // Debug settings
  private readonly disableDesignMaterialSDF: boolean = false;
  private readonly disableDesignMaterialLayers: boolean = false;

  private bakeColorTexture: BakeColorTextureMode = BakeColorTextureMode.DONT_BAKE;

  // We need to asynchronously set the fetchBakedColorTextures flag that affects material loading before the HTTPMaterialFactory can be used.
  // The markReady() function is used to signal that the parameters have been set and we can start loading materials.
  setBakeColorTexture(bakeColorTexture: BakeColorTextureMode) {
    if (this.bakeColorTexture !== bakeColorTexture) {
      this.bakeColorTexture = bakeColorTexture;
      void this.updateLoadOptions();
    }
    this.markReady();
  }

  private readyPromise: Promise<void>;
  private readyPromiseResolve: (() => void) | undefined;
  private markReady() {
    if (this.readyPromiseResolve) {
      this.readyPromiseResolve();
      this.readyPromiseResolve = undefined;
    }
  }

  // The renderer is needed to generate height maps used by design materials.
  constructor(
    private readonly renderer: WebGLRenderer,
    waitForMarkReady: boolean, // Should only be set to false in internal tools that don't depend on asynchronously loaded performance tier parameters.
    private readonly useWebgl2TextureAtlas: boolean = true,
    private readonly enableTopDownLightOffsetMap: boolean = false,
    private readonly offsetMapLightCount: number = 8,
    private forceDetailRenderMode: "SDF" | "ParallaxMap" | null = null,
  ) {
    this.assetLoader = THREEAssetLoader.instance;
    this.materialLoader = HTTPMaterialParameterLoader.instance;

    this.readyPromise = new Promise<void>((resolve) => {
      this.readyPromiseResolve = resolve;
    });
    if (!waitForMarkReady) {
      this.markReady();
    }

    this.hardcodedMaterials[MeshHardcodedMaterialType.INVISIBLE_SHADOW_MESH] = this.buildMeshPhysicalMaterial({
      ...this.defaultMaterialParams,
      side: BackSide,
      colorWrite: false,
      depthWrite: false,
    });
    this.hardcodedMaterials[MeshHardcodedMaterialType.DEBUG_RED_MESH] = this.buildMeshPhysicalMaterial({
      ...this.defaultMaterialParams,
      color: 0xff0000,
    });
    this.hardcodedMaterials[MeshHardcodedMaterialType.DEBUG_WHITE_MESH] = this.buildMeshPhysicalMaterial({
      ...this.defaultMaterialParams,
      color: 0xffffff,
    });
    this.hardcodedMaterials[MeshHardcodedMaterialType.DESELECTED_OVERRIDE] = this.buildMeshPhysicalMaterial({
      color: 0x7b7b7b,
      emissive: 0xffffff,
      emissiveIntensity: 0.1,
    });
  }

  private buildMeshPhysicalMaterial(props: MeshPhysicalMaterialParameters, disableSSAO?: boolean) {
    const material = createMaterialForParameters(props);
    assignSharedDefines(MaterialSharedParameterGroup.instance, material);

    if (props.color !== undefined) {
      material.color.set(props.color);
    }
    if (props.colorWrite !== undefined) {
      material.colorWrite = props.colorWrite;
    }
    if (props.depthWrite !== undefined) {
      material.depthWrite = props.depthWrite;
    }
    if (props.side !== undefined) {
      material.side = props.side;
    }
    if (props.emissive !== undefined) {
      material.emissive.set(props.emissive);
    }
    if (props.emissiveIntensity !== undefined) {
      material.emissiveIntensity = props.emissiveIntensity;
    }

    material.onBeforeCompile = (shader: OnBeforeCompileShader) => {
      includeCommonMaterialUniforms(shader);

      includeAdvancedPCFShadows(shader);

      includeScreenspaceFX(shader);

      includeLightProbes(shader);

      includeSsfxRendererOutput(shader);
    };

    (material as unknown as ExtendedMaterial).onProgramChange = (params: { uniforms: { [name: string]: any } }) => {
      MaterialSharedParameterGroup.instance.useSharedUniformsInMaterial(params.uniforms);
    };

    if (disableSSAO && material.defines) {
      material.defines["DISABLE_SSAO"] = "";
    }
    return material;
  }

  getHardcodedMaterial(id: MeshHardcodedMaterialType) {
    return this.hardcodedMaterials[id];
  }

  designMaterialIsFetched(materialId: MaterialSrc, hasSSFXRenderer: boolean): boolean {
    const cacheKey = loadedMaterialCacheKey(materialId, hasSSFXRenderer);
    return this.designMaterialCache[cacheKey]?.state === "fulfilled";
  }

  /**
   * Get a material with one of the predefined debug colors.
   * @param colorId One of the colors in DebugMaterialColors.
   * @returns A design material with the given color.
   */
  async getDesignMaterialWithColor(colorId: number, hasSSFXRenderer: boolean): Promise<Material | undefined> {
    console.assert(DebugMaterialColors.includes(colorId));
    return await this.getDesignMaterial({ ...DesignMaterialPath.Color.C, colorId: colorId }, hasSSFXRenderer);
  }

  private getLoadOptions(): DesignMaterialLoadOptions {
    let forceDetailRenderMode: DetailRenderMode | undefined = undefined;
    if (this.forceDetailRenderMode !== null) {
      forceDetailRenderMode = DetailRenderMode[this.forceDetailRenderMode];
    }

    return {
      ...defaultDesignMaterialLoadOptions,
      disableSDF: this.disableDesignMaterialSDF,
      disableLayers: this.disableDesignMaterialLayers,
      enableLightOffsetMap: this.enableTopDownLightOffsetMap,
      offsetMapLightCount: this.offsetMapLightCount,
      forceDetailRenderMode,
      bakeColorTexture: this.bakeColorTexture,
      useWebgl2TextureAtlas: this.useWebgl2TextureAtlas,
    };
  }

  async getDesignMaterial(
    materialSrc: MaterialSrc,
    hasSSFXRenderer: boolean,
    forceRefresh = false,
  ): Promise<Material | undefined> {
    const cacheKey = loadedMaterialCacheKey(materialSrc, hasSSFXRenderer);
    if (materialSrc === null) {
      return undefined;
    }

    if (!forceRefresh && this.designMaterialCache.hasOwnProperty(cacheKey)) {
      return this.designMaterialCache[cacheKey].material;
    }

    this.designMaterialCache[cacheKey] = {
      state: "pending",
      hasSSFXRenderer,
      materialSrc,
      material: (async () => {
        try {
          const params = await this.materialLoader.fetchDesignMaterialParams(materialSrc);
          await this.readyPromise; // Make sure that the settings for fetching baked textures are set.
          if (params === null) {
            console.error("Could not make design material", logMaterialSrc(materialSrc));
            this.designMaterialCache[cacheKey].state = "rejected";
            return this.getHardcodedMaterial(failureMaterialType);
          }
          const material = await designMaterialNew(
            params,
            this.renderer,
            this.getTexture.bind(this),
            hasSSFXRenderer,
            materialSrc,
            this.getLoadOptions(),
          );

          const templateJSONPath = MaterialTemplateIdToJson[materialSrc.templateId];
          material.name = templateJSONPath.replace(/^.*[\\\/]/, "");
          this.designMaterialCache[cacheKey].state = "fulfilled";
          return material;
        } catch (e) {
          checkWebGLContextLost(this.renderer, e, "Could not make design material." + logMaterialSrc(materialSrc));
          this.designMaterialCache[cacheKey].state = "rejected";
          return this.getHardcodedMaterial(failureMaterialType);
        }
      })(),
    };

    return this.designMaterialCache[cacheKey].material;
  }

  private async updateLoadOptions() {
    const loadOptions = this.getLoadOptions();
    const promises: Promise<void>[] = [];
    for (const materialCache of Object.values(this.designMaterialCache)) {
      const material = await materialCache.material;
      // Skip if failureMaterialType
      if (materialCache.state === "fulfilled") {
        promises.push(
          setDesignMaterialOptions(
            material as MeshPhysicalMaterial,
            this.renderer,
            materialCache.hasSSFXRenderer,
            this.getTexture.bind(this),
            materialCache.materialSrc,
            loadOptions,
          )[1],
        );
      }
    }
    await Promise.all(promises);
  }

  initGUI(gui, onChange: () => void) {
    if (!gui) {
      return;
    }

    const materialLoadGUI = gui.addFolder("Material load options");
    materialLoadGUI.add(this, "forceDetailRenderMode", [null, "SDF", "ParallaxMap"]).onChange(async () => {
      await this.updateLoadOptions();
      onChange();
    });
    materialLoadGUI
      .add(this, "bakeColorTexture", [
        BakeColorTextureMode.DONT_BAKE,
        BakeColorTextureMode.REPEATING_LAYERS,
        BakeColorTextureMode.FETCH_FROM_AWS,
        BakeColorTextureMode.BAKE,
        BakeColorTextureMode.FLAT_COLOR,
      ])
      .onChange(async () => {
        await this.updateLoadOptions();
        onChange();
      });
  }

  /**
   * @returns The texture and a promise that will complete once the texture has actually finished loading.
   */
  private getTexture(url: string, overrideFormat?: PixelFormat): [TextureType | null, Promise<void>] {
    if (!url) {
      return [null, new Promise<void>(() => {})];
    }

    if (this.textureCache.hasOwnProperty(url)) {
      return this.textureCache[url];
    }

    const texture = new Texture();
    texture.wrapS = RepeatWrapping;
    texture.wrapT = RepeatWrapping;
    texture.anisotropy = DEFAULT_TEXTURE_ANISOTROPY;
    const loadedPromise = new Promise<void>((resolve, reject) => {
      void (async () => {
        await this.assetLoader.loadTextureDirect({
          texture,
          url,
          onLoad: () => {
            resolve();
          },
          onError: () => {
            console.error("could not load texture: " + url);
            reject();
          },
          overrideFormat,
        });
      })();
    });
    this.textureCache[url] = [texture, loadedPromise];
    return [texture, loadedPromise];
  }
}
